import { useEffect } from "react";
import Alert from "../../../../components/Alert";
import JobListCard from "../Components/JobListCard";
import { useJobsListViewModel } from "../viewmodel/job.viewmodel";
import {
  setSearchTerm,
  setStatusFilter,
  setCurrentPage,
  setViewMode,
  setError,
  updateFormField,
  hideToast,
  setDepartment,
  setTitle,
  setLocation,
  setLimit,
} from "../viewmodel/job.action";
import TablePagination from "../Components/TablePagination";
import JobStatsSection from "./JobStatsSection";
import { JobStatus } from "../model/jobEnums";
import { Job } from "../model/JobModel";
import SearchJobsFilter from "./SearchJobsFilter";
import JobGridCard from "../Components/JobsGridCard";
import { ConfirmDeleteModal } from "../../../../components/ConfirmationPopUp";
import Toast from "../../../../components/Toast";

interface JobPostingProps {
  onEditJob: (job: Job) => void;
  onCreateFromDraft: (job: Job) => void;
}
export function JobPosting({ onEditJob, onCreateFromDraft }: JobPostingProps) {
  const {
    state,
    dispatch,
    fetchJobs,
    loadUsers,
    deleteJobsData,
    fetchPausedJob,
    fetchResumeJob,
    fetchCloseJob,
  } = useJobsListViewModel();
  const JobStatusMap: Record<JobStatus, string> = {
    [JobStatus.Draft]: "Draft",
    [JobStatus.Approved]: "Approved",
    [JobStatus.Paused]: "Paused",
    [JobStatus.PendingPlatform]: "Pending Platform",
    [JobStatus.Rejected]: "Rejected",
    [JobStatus.UnderReview]: "Under Review",
    [JobStatus.Published]: "Published",
    [JobStatus.Closed]: "Closed",
  };
  const handleConfirmDelete = async () => {
    await deleteJobsData();
  };
  // Common method to build params and call fetchJobs
  const fetchJobsWithParams = (
    page = 1,
    searchTerm = state.searchTerm,
    statusFilter = state.statusFilter,
  ) => {
    const params: any = {};

    if (state.searchTerm) {
      params.search_query = state.searchTerm;
    }

    if (state.statusFilter !== "all") {
      params.status = state.statusFilter;
    }
    if (state.visibility) {
      params.visibility = state.visibility;
    }

    if (state.hiringManagerId) {
      params.hiring_manager_id = state.hiringManagerId;
    }
    if (state.recruiterId) {
      params.recruiter_id = state.recruiterId;
    }
    params.limit = state.limit;
    params.offset = (page - 1) * state.limit;
    console.log("SEARCH PARAMS:", params);
    fetchJobs(params);
  };

  // Effect to fetch jobs on page change or filter change
  useEffect(() => {
    fetchJobsWithParams(state.currentPage);
    // console.log("API PARAMS", params);
  }, [
    state.currentPage,
    state.statusFilter,
    state.visibility,
    state.department,
    state.query,
    state.location,
    state.hiringManagerId,
    state.recruiterId,
    state.limit,
  ]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadUsers();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div>
      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
        <SearchJobsFilter
          searchTerm={state.searchTerm}
          onSearchTermChange={(value) => dispatch(setSearchTerm(value))}
          statusFilter={state.statusFilter}
          onStatusFilterChange={(value) => dispatch(setStatusFilter(value))}
          jobStatusMap={JobStatusMap}
          jobStatusList={Object.values(JobStatus)}
          loading={state.loading}
          onSearchClick={() => {
            dispatch(setCurrentPage(1));
            dispatch({ type: "SET_JOBS", payload: [] });
            fetchJobsWithParams(1, state.searchTerm, state.statusFilter);
          }}
          // onRefresh={fetchJobs} // Uncomment if refresh needed
        />
      </div>
      <JobStatsSection state={state} />
      {/* {state.loading ? loadingSkeleton : null} */}
      {/* Stats */}
      {/* Error Message */}
      {state.error && (
        <Alert
          type="error"
          message={state.error}
          onClose={() => dispatch(setError(null))}
          className="mb-6"
        />
      )}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-4">
          <h3 className="text-lg font-medium text-gray-900">
            Jobs ({state.total})
          </h3>
        </div>
        <div className="hidden sm:flex items-center space-x-2">
          <span className="text-sm text-gray-500">View:</span>
          <div className="flex rounded-lg border border-gray-300">
            <button
              onClick={() => dispatch(setViewMode("list"))}
              className={`px-3 py-1 text-sm font-medium rounded-l-lg ${
                state.viewMode === "list"
                  ? "bg-indigo-600 text-white"
                  : "bg-white text-gray-700 hover:bg-gray-50"
              }`}
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 10h16M4 14h16M4 18h16"
                />
              </svg>
            </button>
            <button
              onClick={() => dispatch(setViewMode("grid"))}
              className={`px-3 py-1 text-sm font-medium rounded-r-lg ${
                state.viewMode === "grid"
                  ? "bg-indigo-600 text-white"
                  : "bg-white text-gray-700 hover:bg-gray-50"
              }`}
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        <div>
          {state.loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4">
              {Array.from({ length: 8 }).map((_, index) => (
                <div
                  key={index}
                  className="bg-white rounded-lg shadow-sm border p-4 animate-pulse"
                >
                  <div className="flex items-center space-x-3 mb-3">
                    <div className="w-10 h-10 bg-gray-200 rounded-full"></div>
                    <div className="flex-1">
                      <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                      <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div className="h-3 bg-gray-200 rounded"></div>
                    <div className="h-3 bg-gray-200 rounded w-2/3"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : state.jobs.length === 0 ? (
            <div className="px-6 py-12 text-center">
              <h3 className="text-sm font-medium text-gray-900">
                No jobs found
              </h3>
              <p className="text-sm text-gray-500">
                Try adjusting your filters.
              </p>
            </div>
          ) : (
            <>
              {state.viewMode === "grid" && (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4">
                  {state.jobs.map((job) => (
                    <JobGridCard
                      key={job.id}
                      job={job}
                      onEdit={() => onEditJob(job)}
                      openMenuJobId={state.openMenuJobId}
                      dispatch={dispatch}
                      onDelete={(id) =>
                        dispatch({
                          type: "OPEN_DELETE_DIALOG",
                          payload: id,
                        })
                      }
                      onPause={(id) => {
                        fetchPausedJob(id);
                      }}
                      onResume={(id) => {
                        fetchResumeJob(id);
                      }}
                      onClose={(id) => {
                        fetchCloseJob(id);
                      }}
                      onCreateFromDraft={onCreateFromDraft}
                    />
                  ))}
                </div>
              )}

              {state.viewMode === "list" && (
                <div className="space-y-4">
                  {state.jobs.map((job) => (
                    <JobListCard
                      onDelete={(id) =>
                        dispatch({
                          type: "OPEN_DELETE_DIALOG",
                          payload: id,
                        })
                      }
                      key={job.id}
                      job={job}
                      onEdit={() => onEditJob(job)}
                      openMenuJobId={state.openMenuJobId}
                      dispatch={dispatch}
                      onPause={(id) => {
                        fetchPausedJob(id);
                      }}
                      onResume={(id) => {
                        fetchResumeJob(id);
                      }}
                      onClose={(id) => {
                        fetchCloseJob(id);
                      }}
                      onCreateFromDraft={onCreateFromDraft}
                    />
                  ))}
                </div>
              )}
            </>
          )}
        </div>
        {state.total > state.limit && (
          <TablePagination
            currentPage={state.currentPage}
            totalCount={state.total}
            rowsPerPage={state.limit}
            onPageChange={(page) => dispatch(setCurrentPage(page))}
            onRowsPerPageChange={(rows) => {
              dispatch(setLimit(rows));
              dispatch(setCurrentPage(1));
            }}
            isGrid={state.viewMode === "grid"}
          />
        )}
      </div>
      <ConfirmDeleteModal
        open={state.deleteDialogOpen}
        title="Delete Job?"
        entityName={
          state.jobs.find((p: any) => p.id === state.deletingFlowId)?.title
        }
        loading={state.loading}
        onCancel={() => dispatch({ type: "CLOSE_DELETE_DIALOG" })}
        onConfirm={handleConfirmDelete}
      />
      <Toast
        message={state.toast.message}
        type={state.toast.type}
        isVisible={state.toast.isVisible}
        onClose={hideToast}
      />
    </div>
  );
}
